import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { attendanceService } from '../../api/attendanceService';
import { registrationService } from '../../api/registrationService';
import { getUsersWithRole } from '../../api/roleManagementService';
import { useCategories } from '../../hooks/useCategories';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminUsers, isSuperAdminRole } from '../../utils/reportFilters';
import { masterRows } from '../../utils/inquiryDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button, buttonVariants } from '../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { cn } from '../../utils/cn';
// import * as XLSX from 'xlsx';
import * as XLSX from 'xlsx-js-style';
import {
  Calendar,
  Download,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  Users,
  ChevronDown,
  FileSpreadsheet,
  Search,
  Shield
} from 'lucide-react';

const roleSelectItemClass =
  'hover:!bg-[#E0F8F3] focus:!bg-[#E0F8F3] !text-[#000000] dark:!text-gray-100 [&.font-medium]:!bg-[#E0F8F3] [&.font-medium]:!text-[#000000]';

const AttendanceView = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredRowsCache, setFilteredRowsCache] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('all');
  const [selectedRoleUserIds, setSelectedRoleUserIds] = useState(null);
  const [loadingRoleUsers, setLoadingRoleUsers] = useState(false);
  const [roleUsersError, setRoleUsersError] = useState(false);
  const { data: categoriesRaw = [], isLoading: loadingCategories } = useCategories();
  const categories = useMemo(() => {
    const filteredCategories = categoriesRaw.filter((cat) => {
      const categoryId = cat.categoryId || cat.id;
      const categoryName = cat.categoryName || cat.name || '';
      const isActive =
        cat.isActive !== false &&
        cat.status !== false &&
        String(cat.status || '').toLowerCase() !== 'inactive';
      return (
        isActive &&
        categoryId !== null &&
        categoryId !== undefined &&
        categoryName !== 'Select a category...' &&
        categoryName.trim() !== ''
      );
    });
    return Array.isArray(filteredCategories) ? filteredCategories : [];
  }, [categoriesRaw]);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const skipPageEffectRef = useRef(true);

  // Pagination (screen only — export omits page/size and loads all matches)
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const rolesResp = await registrationService.getRoles();
        if (!cancelled) {
          const rawRoles = Array.isArray(rolesResp?.data) ? rolesResp.data : [];
          setRoles(
            rawRoles.filter((role) => {
              if (role.roleId == null) return false;
              const code = String(role.roleCode || '').toUpperCase();
              if (role.isRequesterRole === true) return false;
              if (code === 'ADMIN') return false;
              if (isSuperAdminRole(code)) return false;
              return true;
            })
          );
        }
      } catch (err) {
        console.warn('Failed to load roles:', err);
        if (!cancelled) {
          setRoles([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRoles(false);
        }
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedRoleId === 'all') {
      setSelectedRoleUserIds(null);
      setRoleUsersError(false);
      setLoadingRoleUsers(false);
      return undefined;
    }

    const roleId = parseInt(selectedRoleId, 10);
    if (Number.isNaN(roleId)) {
      setSelectedRoleUserIds(new Set());
      setRoleUsersError(true);
      setLoadingRoleUsers(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingRoleUsers(true);
    setRoleUsersError(false);
    setSelectedRoleUserIds(null);

    getUsersWithRole(roleId)
      .then((response) => {
        if (cancelled) return;
        const users = masterRows(response);
        const ids = new Set(
          users
            .map((user) => user.userId ?? user.user_id ?? user.id)
            .filter((id) => id != null)
            .map((id) => String(id))
        );
        setSelectedRoleUserIds(ids);
        setRoleUsersError(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Failed to load users for role:', err);
        setSelectedRoleUserIds(new Set());
        setRoleUsersError(true);
        toast.error(extractErrorMessage(err, 'Failed to load users for selected role'));
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingRoleUsers(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRoleId]);
  
  // Custom date range filter
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Refs for date inputs to enable programmatic focus
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  // Get current month date range (capped to today if viewing current month)
  const getMonthDateRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = new Date(year, month, 1);
    let endDate = new Date(year, month + 1, 0); // Last day of the month

    // If viewing current month, cap endDate to today (include today)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const isCurrentMonth = year === todayLocal.getFullYear() && month === todayLocal.getMonth();
    if (isCurrentMonth && endDate > todayLocal) {
      endDate = todayLocal; // Include today
    }

    // Format dates as YYYY-MM-DD (local timezone, not UTC)
    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  // Get tenant ID from storage
  const getTenantId = () => {
    return sessionStorage.getItem('tenantId') || 1;
  };

  // Debounce search so each keystroke does not hit the API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const buildReportParams = ({ includePagination } = { includePagination: true }) => {
    let startDate;
    let endDate;
    if (useCustomDateRange && fromDate && toDate) {
      startDate = fromDate;
      endDate = toDate;
    } else {
      const monthRange = getMonthDateRange(currentMonth);
      startDate = monthRange.startDate;
      endDate = monthRange.endDate;
    }

    const params = {
      startDate,
      endDate,
      tenantId: parseInt(getTenantId(), 10),
    };

    if (selectedCategory !== 'all') {
      params.categoryId = parseInt(selectedCategory, 10);
    }

    const q = debouncedSearchTerm.trim();
    if (q) {
      params.search = q;
    }

    if (includePagination) {
      params.page = currentPage;
      params.size = pageSize;
    }

    return { params, startDate, endDate };
  };

  const unwrapReportPayload = (response) => {
    const root = response?.data?.data ?? response?.data ?? response;
    if (Array.isArray(root)) {
      return { rows: root, pageMeta: null };
    }
    if (root && Array.isArray(root.content)) {
      return {
        rows: root.content,
        pageMeta: {
          totalElements: root.totalElements ?? root.content.length,
          totalPages: root.totalPages ?? 1,
          pageNumber: root.pageNumber ?? 0,
          pageSize: root.pageSize ?? pageSize,
          first: root.first ?? true,
          last: root.last ?? true,
        },
      };
    }
    return { rows: [], pageMeta: null };
  };

  const roleFilterActive = selectedRoleId !== 'all';

  const applyClientPage = useCallback((rows, page) => {
    const totalElements = rows.length;
    const totalPages = totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;
    const safePage = totalPages === 0 ? 0 : Math.min(page, totalPages - 1);
    const start = safePage * pageSize;
    const end = start + pageSize;

    setAttendanceData(rows.slice(start, end));
    setPaginationData({
      totalElements,
      totalPages,
      pageNumber: safePage,
      pageSize,
      first: safePage === 0,
      last: totalPages === 0 || safePage >= totalPages - 1,
    });

    return safePage;
  }, [pageSize]);

  const filterRowsByRole = useCallback((rows) => {
    if (!roleFilterActive || !selectedRoleUserIds) {
      return rows;
    }
    return rows.filter((row) => selectedRoleUserIds.has(String(row.userId)));
  }, [roleFilterActive, selectedRoleUserIds]);

  const loadAttendanceData = useCallback(async () => {
    if (roleFilterActive) {
      if (loadingRoleUsers) return;
      if (roleUsersError || !selectedRoleUserIds) {
        setAttendanceData([]);
        setFilteredRowsCache([]);
        setPaginationData({
          totalElements: 0,
          totalPages: 0,
          pageNumber: 0,
          pageSize,
          first: true,
          last: true,
        });
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      const { params } = buildReportParams({ includePagination: !roleFilterActive });
      const response = await attendanceService.getAttendanceReport(params);
      const { rows, pageMeta } = unwrapReportPayload(response);

      let processedRows = excludeSuperAdminUsers(rows);
      if (roleFilterActive) {
        processedRows = filterRowsByRole(processedRows);
        setFilteredRowsCache(processedRows);
        const safePage = applyClientPage(processedRows, currentPage);
        if (safePage !== currentPage) {
          setCurrentPage(safePage);
        }
      } else {
        setFilteredRowsCache([]);
        setAttendanceData(processedRows);
        setPaginationData(
          pageMeta || {
            totalElements: processedRows.length,
            totalPages: processedRows.length > 0 ? 1 : 0,
            pageNumber: currentPage,
            pageSize,
            first: currentPage === 0,
            last: true,
          }
        );
      }
    } catch (err) {
      console.error('Error loading attendance data:', err);
      setError(extractErrorMessage(err, 'Failed to load attendance data'));
      toast.error(extractErrorMessage(err, 'Failed to load attendance data'));
      setAttendanceData([]);
      setFilteredRowsCache([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  }, [
    roleFilterActive,
    loadingRoleUsers,
    roleUsersError,
    selectedRoleUserIds,
    pageSize,
    filterRowsByRole,
    applyClientPage,
    currentMonth,
    selectedCategory,
    fromDate,
    toDate,
    useCustomDateRange,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    loadAttendanceData();
  }, [loadAttendanceData]);

  useEffect(() => {
    if (skipPageEffectRef.current) {
      skipPageEffectRef.current = false;
      return;
    }
    if (roleFilterActive) {
      const safePage = applyClientPage(filteredRowsCache, currentPage);
      if (safePage !== currentPage) {
        setCurrentPage(safePage);
      }
    } else {
      loadAttendanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };

    if (exportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportMenuOpen]);

  const handlePageChange = (newPage) => {
    if (newPage < 0) return;
    if (paginationData && newPage >= paginationData.totalPages) return;
    setCurrentPage(newPage);
  };

  const handleRoleChange = (value) => {
    setSelectedRoleId(value);
    setCurrentPage(0);
  };

  const handleRoleTriggerFocus = (event) => {
    const trigger = event.currentTarget;
    trigger.style.setProperty('--tw-ring-color', 'transparent');
    trigger.style.setProperty('--tw-ring-shadow', '0 0 #0000');
    trigger.style.setProperty('--tw-ring-offset-shadow', '0 0 #0000');
    trigger.style.boxShadow = 'none';
    trigger.style.outline = 'none';
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedRoleId('all');
    setCurrentPage(0);
  };

  const hasActiveFilters = searchTerm.trim() !== '' || selectedRoleId !== 'all';

  const fetchExportRows = async () => {
    const { params } = buildReportParams({ includePagination: false });
    const response = await attendanceService.getAttendanceReport(params);
    const { rows } = unwrapReportPayload(response);
    let exportRows = excludeSuperAdminUsers(rows);
    if (roleFilterActive && selectedRoleUserIds) {
      exportRows = exportRows.filter((row) => selectedRoleUserIds.has(String(row.userId)));
    }
    return exportRows;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setUseCustomDateRange(false);
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Don't allow navigation to future months
    if (nextMonth <= today) {
      setCurrentMonth(nextMonth);
      setUseCustomDateRange(false);
      setFromDate('');
      setToDate('');
      setCurrentPage(0);
    } else {
      toast.error('Cannot navigate to future months');
    }
  };

  const handleCurrentMonth = () => {
    setCurrentMonth(new Date());
    setUseCustomDateRange(false);
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
  };

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0];
    
    // Validate: don't allow future dates
    if (selectedDate > today) {
      toast.error('From Date cannot be in the future');
      return;
    }
    
    // Validate: fromDate should not be after toDate
    if (toDate && selectedDate > toDate) {
      toast.error('From Date cannot be after To Date');
      return;
    }
    
    setFromDate(selectedDate);
    setUseCustomDateRange(true);
    setCurrentPage(0);
  };

  const handleToDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0];
    
    // Validate: don't allow future dates
    if (selectedDate > today) {
      toast.error('To Date cannot be in the future');
      return;
    }
    
    // Validate: toDate should not be before fromDate
    if (fromDate && selectedDate < fromDate) {
      toast.error('To Date cannot be before From Date');
      return;
    }
    
    setToDate(selectedDate);
    setUseCustomDateRange(true);
    setCurrentPage(0);
  };

  const handleExportPDF = async () => {
    setExportMenuOpen(false);
    try {
      setExporting(true);

      const { params, startDate, endDate } = buildReportParams({ includePagination: false });
      const blob = await attendanceService.downloadAttendanceReportPDF(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Attendance_Report_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(extractErrorMessage(error, 'Failed to export PDF'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    try {
      setExporting(true);

      const exportRows = await fetchExportRows();
      const { startDate, endDate } = buildReportParams({ includePagination: false });

      if (exportRows.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Prepare Excel data with full date format (DD-MM-YYYY) in headers
      const formatDateForExcel = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const headers = ['Employee Name', 'Email', ...monthDates.map(date => formatDateForExcel(date)), 'Present', 'Absent', 'Leave', 'Holiday', 'Total Days'];

      const excelRows = exportRows.map((employee) => {
        const attendanceMap = employee.attendanceMap || {};
        const row = [
          employee.empName || employee.fullName || 'N/A',
          employee.email || 'N/A',
          ...monthDates.map(date => {
            const status = attendanceMap[date] || 'A';
            return status;
          }),
          employee.presentCount || 0,
          employee.absentCount || 0,
          employee.leaveCount || 0,
          employee.holidayCount || 0,
          employee.totalDays || monthDates.length
        ];
        return row;
      });

      // Calculate summary totals (full export set)
      const summaryRow = [
        'TOTAL',
        '',
        ...monthDates.map(() => ''),
        exportRows.reduce((sum, emp) => sum + (emp.presentCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.absentCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.leaveCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.holidayCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.totalDays || monthDates.length), 0)
      ];

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();


      const title = 'Attendance Report';
      const subtitle = `Attendance Report (${startDate} to ${endDate})`;

      // total number of columns
      const totalColumns = headers.length;

      // build sheet data with empty rows
      const sheetData = [
        [title],
        [subtitle],
        [],              // empty row
        headers,         // column headers
        ...excelRows,
        summaryRow
      ];
      const TITLE_ROW_INDEX = 0;       // Row 1
      const SUBTITLE_ROW_INDEX = 1;    // Row 2
      const HEADER_ROW_INDEX = 3;      // Row 4
      const DATA_START_ROW_INDEX = 4;  // Row 5 (FIRST DATA ROW)

      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      ws['!merges'] = [
        {
          s: { r: 0, c: 0 },
          e: { r: 0, c: totalColumns - 1 }
        },
        {
          s: { r: 1, c: 0 },
          e: { r: 1, c: totalColumns - 1 }
        }
      ];

      // Title (Row 1)
      ws['A1'].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: 'center', vertical: 'center' }
      };

      // Subtitle (Row 2)
      ws['A2'].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: 'center', vertical: 'center' }
      };



      // Set column widths for better readability
      const colWidths = [
        { wch: 25 }, // Employee Name
        { wch: 30 }, // Email
        ...monthDates.map(() => ({ wch: 13 })), // Date columns
        { wch: 10 }, // Present
        { wch: 10 }, // Absent
        { wch: 10 }, // Leave
        { wch: 10 }, // Holiday
        { wch: 10 }  // Total Days
      ];
      ws['!cols'] = colWidths;

      const lastColIndex = headers.length - 1;

      // Apply autofilter ONLY on header row
      ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: HEADER_ROW_INDEX, c: 0 },          // A4 (Employee Name)
          e: { r: HEADER_ROW_INDEX, c: lastColIndex } // Last header column
        })
      };
      

      // Freeze panes: Freeze first row (headers) and first column (Employee Name)
      ws['!freeze'] = {
        xSplit: 1,  // Freeze first column
        ySplit: 1,  // Freeze first row
        topLeftCell: 'B5',  // Top-left visible cell after freeze
        activePane: 'bottomRight',
        state: 'frozen'
      };
      // Alternative method using views
      ws['!views'] = [{
        state: 'frozen',
        xSplit: 1,
        ySplit: 1,
        topLeftCell: 'B5',
        activeCell: 'B2'
      }];

      // Add conditional formatting for status cells (P/A/L/H colors)
      // Note: xlsx library has limited conditional formatting support
      // We'll add cell styles directly for status columns
      const statusColorMap = {
        'P': { fill: { fgColor: { rgb: 'C6EFCE' } }, font: { color: { rgb: '006100' }, bold: true } }, // Light green
        'A': { fill: { fgColor: { rgb: 'FFC7CE' } }, font: { color: { rgb: '9C0006' }, bold: true } }, // Light red
        'L': { fill: { fgColor: { rgb: 'FFEB9C' } }, font: { color: { rgb: '9C6500' }, bold: true } }, // Light yellow
        'H': { fill: { fgColor: { rgb: 'BDD7EE' } }, font: { color: { rgb: '002060' }, bold: true } }  // Light blue
      };

      // Apply colors and center alignment to status cells (date columns)
      const dateStartCol = 2; // Column C (0-indexed: A=0, B=1, C=2)
      excelRows.forEach((row, rowIndex) => {
        monthDates.forEach((date, dateIndex) => {
          const colIndex = dateStartCol + dateIndex;
          const cellAddress = XLSX.utils.encode_cell({
            r: DATA_START_ROW_INDEX + rowIndex,
            c: colIndex
          });
          const status = row[dateIndex + 2]; // +2 for Employee Name and Email columns

          if (status && statusColorMap[status.toUpperCase()]) {
            if (!ws[cellAddress]) {
              ws[cellAddress] = { t: 's', v: status };
            }
            // Apply color and center alignment
            ws[cellAddress].s = {
              ...statusColorMap[status.toUpperCase()],
              alignment: { horizontal: 'center', vertical: 'center' }
            };
          }
        });
      });

      // Center-align all data cells (employee name, email, and summary columns)
      const totalRows = excelRows.length;
      headers.forEach((header, colIndex) => {
        // Skip header row (already styled) and summary row (will be styled separately)
        for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
          const cellAddress = XLSX.utils.encode_cell({
            r: DATA_START_ROW_INDEX + rowIndex,
            c: colIndex
          });
          if (ws[cellAddress]) {
            // If cell doesn't have style, create it
            if (!ws[cellAddress].s) {
              ws[cellAddress].s = {};
            }
            // Ensure center alignment
            if (!ws[cellAddress].s.alignment) {
              ws[cellAddress].s.alignment = {};
            }
            ws[cellAddress].s.alignment.horizontal = 'center';
            ws[cellAddress].s.alignment.vertical = 'center';
          }
        }
      });

      // Style header row - Bold and center-aligned
      // Style header row - Bold only (no background color)
      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: HEADER_ROW_INDEX, c: colIndex });

        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: header };
        }
        ws[cellAddress].s = {
          font: { bold: true }, // ✅ ONLY BOLD
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true
          }
        };
      });


      // Style summary row (last row) - Bold and center-aligned
      const summaryRowIndex = DATA_START_ROW_INDEX + excelRows.length;
      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: summaryRowIndex, c: colIndex });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '70AD47' } }, // Green background
            alignment: { horizontal: 'center', vertical: 'center' }
          };
        }
      });

      // Add Excel Table (ListObject) - This creates a formatted table with built-in filters
      // Note: xlsx library doesn't directly support Excel Tables, but autofilter provides similar functionality
      // The autofilter we added above will provide the filtering capability

      // 🔒 FINAL SAFETY PASS – force center alignment everywhere
      const range = XLSX.utils.decode_range(ws['!ref']);

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;

          ws[cellAddress].s = ws[cellAddress].s || {};
          ws[cellAddress].s.alignment = {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true
          };
        }
      }


      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Attendance_Report_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Excel file downloaded successfully with filters and formatting');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(extractErrorMessage(error, 'Failed to export Excel'));
    } finally {
      setExporting(false);
    }
  };

  // Generate all dates in the range (custom date range or month range)
  const monthDates = useMemo(() => {
    let startDate, endDate;
    
    // Use custom date range if enabled, otherwise use month range
    if (useCustomDateRange && fromDate && toDate) {
      startDate = fromDate;
      endDate = toDate;
    } else {
      const monthRange = getMonthDateRange(currentMonth);
      startDate = monthRange.startDate;
      endDate = monthRange.endDate;
    }
    
    const dates = [];

    // Parse dates as local dates (not UTC) to avoid timezone issues
    const startParts = startDate.split('-');
    const endParts = endDate.split('-');
    const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
    const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));

    // Get today's date in local timezone (reset time to 00:00:00)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Only include dates up to and including today
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      // Create date object with only date part (no time)
      const currentDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      // Include dates that are today or in the past (use <= to include today)
      // Compare only the date part, ignoring time
      if (currentDate.getTime() <= todayLocal.getTime()) {
        // Format as YYYY-MM-DD for consistency
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }
    }

    return dates;
  }, [currentMonth, useCustomDateRange, fromDate, toDate]);

  // Format date for display (day number only) - kept for backward compatibility
  const formatDay = (dateString) => {
    const date = new Date(dateString);
    return date.getDate().toString().padStart(2, '0');
  };

  // Format date for display with full date (DD-MM-YYYY)
  const formatDateFull = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'P':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'A':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'L':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'H':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case 'P':
        return 'Present';
      case 'A':
        return 'Absent';
      case 'L':
        return 'Leave';
      case 'H':
        return 'Holiday';
      default:
        return status || '-';
    }
  };

  // Format month display
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Format date range for display
  const formatDateRange = () => {
    if (useCustomDateRange && fromDate && toDate) {
      const formatDisplayDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      return `${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`;
    }
    return formatMonthYear(currentMonth);
  };

  // Check if the month is in the future
  const isFutureMonth = (date) => {
    const today = new Date();
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return monthStart > currentMonthStart;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Attendance View</h1>
          <p className="text-gray-600 mt-1">View monthly attendance for all employees</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative" ref={exportMenuRef}>
            <Button
              variant="outline"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={exporting || loading}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[160px]">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  onClick={handleExportPDF}
                  disabled={exporting || loading}
                >
                  <FileText className="h-4 w-4" />
                  Export PDF
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  onClick={handleExportExcel}
                  disabled={exporting || loading}
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Export Excel
                </button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={loadAttendanceData}
            disabled={loading || loadingRoleUsers}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading || loadingRoleUsers ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            {/* Month Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousMonth}
                disabled={loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-md border">
                <Calendar className="h-4 w-4 text-gray-600" />
                <span className="font-medium text-gray-900 min-w-[150px] text-center">
                  {formatMonthYear(currentMonth)}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                disabled={loading || isFutureMonth(currentMonth)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCurrentMonth}
                disabled={loading || loadingRoleUsers}
              >
                Current Month
              </Button>
            </div>

            <div className="relative w-[220px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
                disabled={loadingRoleUsers}
                className="h-9 pl-10"
              />
            </div>

            <Select
              value={selectedRoleId}
              onValueChange={handleRoleChange}
              disabled={loadingRoles || loading || loadingRoleUsers}
            >
              <SelectTrigger
                onFocus={handleRoleTriggerFocus}
                className={cn(
                  '!h-9 !min-w-[200px] !w-[220px] !max-w-[260px] !border-primary gap-2 px-3 text-sm font-medium rounded-xl text-[#000000]',
                  buttonVariants.outline,
                  'shadow-none focus:shadow-none focus-visible:shadow-none',
                  'focus:outline-none focus-visible:outline-none',
                  '!focus:ring-0 !focus:ring-offset-0 !focus:ring-transparent focus:[--tw-ring-color:transparent]',
                  'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent focus-visible:[--tw-ring-color:transparent]',
                  '!focus:border-primary focus-visible:border-primary active:border-primary'
                )}
              >
                <Shield className="h-4 w-4 shrink-0" />
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="rounded-xl min-w-[220px]">
                <SelectItem value="all" className={roleSelectItemClass}>All Roles</SelectItem>
                {roles.map((role) => (
                  <SelectItem
                    key={role.roleId}
                    value={String(role.roleId)}
                    className={roleSelectItemClass}
                  >
                    {role.label || role.roleCode}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={handleClearFilters} className="h-9">
                Clear
              </Button>
            )}

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Category:</label>
              <Select
                value={selectedCategory}
                onValueChange={(value) => {
                  setSelectedCategory(value);
                  setCurrentPage(0);
                }}
                disabled={loadingCategories || loading || loadingRoleUsers}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.categoryId || category.id} value={String(category.categoryId || category.id)}>
                      {category.categoryName || category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom Date Range Filter */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <style>{`
              /* Hide default browser calendar picker indicator for date inputs in this component */
              .attendance-date-input::-webkit-calendar-picker-indicator {
                display: none !important;
                -webkit-appearance: none !important;
                opacity: 0 !important;
                width: 0 !important;
                height: 0 !important;
              }
              .attendance-date-input::-webkit-inner-spin-button,
              .attendance-date-input::-webkit-clear-button {
                display: none !important;
                -webkit-appearance: none !important;
              }
            `}</style>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">From Date:</label>
                <div className="relative">
                  <Calendar 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 z-10" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = fromDateInputRef.current;
                      // Ensure we have a valid input element with focus method
                      if (input && typeof input.focus === 'function') {
                        input.focus();
                        // Try to show the native date picker if available (modern browsers)
                        if (input.showPicker && typeof input.showPicker === 'function') {
                          try {
                            const pickerPromise = input.showPicker();
                            // Only call catch if it returns a Promise
                            if (pickerPromise && typeof pickerPromise === 'object' && typeof pickerPromise.catch === 'function') {
                              pickerPromise.catch(() => {
                                // Fallback to focus if showPicker fails
                                if (input && typeof input.focus === 'function') {
                                  input.focus();
                                }
                              });
                            }
                          } catch (error) {
                            // If showPicker throws an error, just focus
                            if (input && typeof input.focus === 'function') {
                              input.focus();
                            }
                          }
                        }
                      }
                    }}
                  />
                  <Input
                    ref={fromDateInputRef}
                    type="date"
                    value={fromDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={handleFromDateChange}
                    disabled={loading}
                    className="attendance-date-input pl-10 pr-3 w-[180px]"
                    style={{
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">To Date:</label>
                <div className="relative">
                  <Calendar 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 z-10" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = toDateInputRef.current;
                      // Ensure we have a valid input element with focus method
                      if (input && typeof input.focus === 'function') {
                        input.focus();
                        // Try to show the native date picker if available (modern browsers)
                        if (input.showPicker && typeof input.showPicker === 'function') {
                          try {
                            const pickerPromise = input.showPicker();
                            // Only call catch if it returns a Promise
                            if (pickerPromise && typeof pickerPromise === 'object' && typeof pickerPromise.catch === 'function') {
                              pickerPromise.catch(() => {
                                // Fallback to focus if showPicker fails
                                if (input && typeof input.focus === 'function') {
                                  input.focus();
                                }
                              });
                            }
                          } catch (error) {
                            // If showPicker throws an error, just focus
                            if (input && typeof input.focus === 'function') {
                              input.focus();
                            }
                          }
                        }
                      }
                    }}
                  />
                  <Input
                    ref={toDateInputRef}
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={handleToDateChange}
                    disabled={loading}
                    className="attendance-date-input pl-10 pr-3 w-[180px]"
                    style={{
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                </div>
              </div>

              {(fromDate || toDate) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFromDate('');
                    setToDate('');
                    setUseCustomDateRange(false);
                    setCurrentPage(0);
                  }}
                  disabled={loading}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear Date Range
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-800">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Attendance Report - {formatDateRange()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading || (roleFilterActive && loadingRoleUsers) ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-600">Loading attendance data...</span>
            </div>
          ) : attendanceData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mb-4 text-gray-400" />
              <p className="text-lg font-medium">
                {debouncedSearchTerm.trim() || roleFilterActive
                  ? 'No employees match your filters'
                  : 'No attendance data found'}
              </p>
              <p className="text-sm mt-1">
                {debouncedSearchTerm.trim() || roleFilterActive
                  ? 'Try a different search term or role'
                  : 'Try selecting a different month or category'}
              </p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 border-r border-gray-200 min-w-[200px]">
                      Employee Name
                    </th>
                    {monthDates.map((date) => (
                      <th
                        key={date}
                        className="px-2 py-3 text-center text-xs font-semibold text-gray-700 border-r border-gray-200 min-w-[100px]"
                        title={new Date(date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      >
                        {formatDateFull(date)}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 bg-gray-100">
                      Summary
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData.map((employee, idx) => {
                    const attendanceMap = employee.attendanceMap || {};
                    return (
                      <tr
                        key={employee.userId || idx}
                        className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-200">
                          <div className="flex flex-col">
                            <span>{employee.empName || employee.fullName || 'N/A'}</span>
                            {employee.email && (
                              <span className="text-xs text-gray-500">{employee.email}</span>
                            )}
                          </div>
                        </td>
                        {monthDates.map((date) => {
                          // Get status from attendance map, default to 'A' (Absent)
                          // The date key should match exactly as YYYY-MM-DD format
                          const status = attendanceMap[date] || 'A';
                          return (
                            <td
                              key={date}
                              className="px-2 py-3 text-center border-r border-gray-200"
                            >
                              <Badge
                                className={`${getStatusColor(status)} text-xs font-medium px-1.5 py-0.5`}
                                title={getStatusLabel(status)}
                              >
                                {status}
                              </Badge>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-center text-sm bg-gray-50">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-gray-600">P:</span>
                              <span className="font-semibold text-green-700">
                                {employee.presentCount || 0}
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-gray-600">A:</span>
                              <span className="font-semibold text-red-700">
                                {employee.absentCount || 0}
                              </span>
                            </div>
                            {employee.leaveCount > 0 && (
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-xs text-gray-600">L:</span>
                                <span className="font-semibold text-yellow-700">
                                  {employee.leaveCount || 0}
                                </span>
                              </div>
                            )}
                            {employee.holidayCount > 0 && (
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-xs text-gray-600">H:</span>
                                <span className="font-semibold text-blue-700">
                                  {employee.holidayCount || 0}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {paginationData && paginationData.totalElements > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {currentPage * pageSize + 1} to{' '}
                  {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                  {paginationData.totalElements} employees
                </div>
                {paginationData.totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(0)}
                      disabled={currentPage === 0 || loading}
                      className="hidden sm:flex"
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0 || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {(() => {
                        const pages = [];
                        const totalPages = paginationData.totalPages;
                        const current = currentPage + 1;
                        if (totalPages <= 7) {
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(
                              <Button
                                key={i}
                                variant={i === current ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handlePageChange(i - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {i}
                              </Button>
                            );
                          }
                        } else {
                          pages.push(
                            <Button
                              key={1}
                              variant={current === 1 ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(0)}
                              disabled={loading}
                              className="min-w-[2.5rem]"
                            >
                              1
                            </Button>
                          );
                          if (current > 3) {
                            pages.push(
                              <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                ...
                              </span>
                            );
                          }
                          const start = Math.max(2, current - 1);
                          const end = Math.min(totalPages - 1, current + 1);
                          for (let i = start; i <= end; i++) {
                            if (i !== 1 && i !== totalPages) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          }
                          if (current < totalPages - 2) {
                            pages.push(
                              <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <Button
                              key={totalPages}
                              variant={current === totalPages ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(totalPages - 1)}
                              disabled={loading}
                              className="min-w-[2.5rem]"
                            >
                              {totalPages}
                            </Button>
                          );
                        }
                        return pages;
                      })()}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= paginationData.totalPages - 1 || loading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(paginationData.totalPages - 1)}
                      disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      className="hidden sm:flex"
                    >
                      Last
                    </Button>
                  </div>
                )}
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Legend:</span>
            <Badge className="bg-green-100 text-green-800 border-green-300">P - Present</Badge>
            <Badge className="bg-red-100 text-red-800 border-red-300">A - Absent</Badge>
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">L - Leave</Badge>
            <Badge className="bg-blue-100 text-blue-800 border-blue-300">H - Holiday</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendanceView;
